import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import ReactTooltip from 'react-tooltip'

import Icon from '../../../Icons'

const renderTooltipLines = (t, key) =>
  t(`create-event:selectEventType.${key}`, { returnObjects: true }).map((line) => (
    <div key={line}>{line}</div>
  ))

const getTypes = (t) => ({
  eventType: {
    type: 'event',
    title: t('create-event:selectEventType.buildingEventTitle'),
    tooltip: () => <React.Fragment>{renderTooltipLines(t, 'buildingEventTooltip')}</React.Fragment>,
    icon: 'buildingEvent',
    svgProps: () => ({
      fill: 'black',
    }),
  },
  classEventType: {
    type: 'class',
    title: t('create-event:selectEventType.classTitle'),
    tooltip: () => <React.Fragment>{renderTooltipLines(t, 'classTooltip')}</React.Fragment>,
    icon: 'classEvent',
    svgProps: () => ({
      fill: 'black',
      fillRule: 'evenodd',
      clipRule: 'evenodd',
    }),
  },
  appointmentType: {
    type: 'appointment',
    title: t('create-event:selectEventType.appointmentTitle'),
    tooltip: () => <React.Fragment>{renderTooltipLines(t, 'appointmentTooltip')}</React.Fragment>,
    icon: 'appointmentEvent',
    svgProps: () => ({
      stroke: 'black',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
    }),
  },
  reminderType: {
    type: 'reminder',
    title: t('create-event:selectEventType.reminderTitle'),
    tooltip: () => <React.Fragment>{renderTooltipLines(t, 'reminderTooltip')}</React.Fragment>,
    svgProps: () => ({
      stroke: 'black',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      fill: 'none',
    }),
    icon: 'reminderEvent',
  },
})

const getAvailableTypes = (t) => {
  const types = getTypes(t)
  return {
    propertyManager: [types.eventType, types.reminderType],
    trainer: [types.appointmentType],
    shiftManager: [types.eventType, types.reminderType],
    all: Object.values(types),
  }
}

const renderButton = ({ type, title, activeEventType, handleChange, tooltip, icon, svgProps }) => {
  const isSelected = activeEventType === type
  return (
    <div
      key={type}
      className={`option ${isSelected ? 'selected' : ''}`}
      role="button"
      onClick={() => handleChange(type)}
    >
      <div>
        <Icon
          icon={icon}
          styles={{ width: '50px', height: '50px' }}
          viewBox="0 0 40 40"
          svgProps={{ ...svgProps() }}
        />
      </div>
      <div className="option__title">
        {title}
        <span data-tip data-for={`${type}-triggerTip`}>
          <Icon
            icon="exclamationPoint"
            styles={{
              width: '20px',
              height: '20px',
            }}
            svgProps={{ strokeWidth: '2px', fill: 'black' }}
          />
          <ReactTooltip
            className="tooltip"
            id={`${type}-triggerTip`}
            multiline
            place="right"
            effect="solid"
          >
            {tooltip()}
          </ReactTooltip>
        </span>
      </div>
    </div>
  )
}

renderButton.propTypes = {
  type: PropTypes.string,
  title: PropTypes.string,
  handleChange: PropTypes.func,
  tooltip: PropTypes.func,
  icon: PropTypes.string,
  activeEventType: PropTypes.string,
  svgProps: PropTypes.func,
}

const getAvailableEventTypes = (role, t) => {
  const availableTypes = getAvailableTypes(t)
  switch (role) {
    case 'PropertyManager':
      return availableTypes.propertyManager
    case 'Trainer':
      return availableTypes.trainer
    case 'ShiftManager':
      return availableTypes.shiftManager
  }
  return availableTypes.all
}

const SelectEventType = ({ role, handleClose }) => {
  const { t } = useTranslation(['create-event', 'common'])
  const navigate = useNavigate()

  const [activeEventType, setActiveEventType] = useState(null)
  const handleChange = (type) => {
    setActiveEventType(type)
  }

  const handleClick = () => {
    navigate(`/dashboard/calendar/${activeEventType}`)
  }
  return (
    <div className="events-modal">
      <div className="events-modal-card __select-card">
        <div>
          <h1>{t('create-event:selectEventType.createAnEvent')}</h1>
          <hr />
          <h1>{t('create-event:selectEventType.selectEventTypePrompt')}</h1>
        </div>
        <div className="options-container">
          {getAvailableEventTypes(role, t).map((type) =>
            renderButton({ ...type, activeEventType, handleChange }),
          )}
        </div>
        <div className="select-event-footer">
          <button className="button button-transparent" onClick={handleClose}>
            {t('common:actions.cancel')}
          </button>
          <button
            className="button button-primary auto-width"
            onClick={handleClick}
            disabled={!activeEventType}
          >
            {t('create-event:selectEventType.createAnEvent')}
          </button>
        </div>
      </div>
    </div>
  )
}

SelectEventType.propTypes = {
  role: PropTypes.string,
  handleClose: PropTypes.func,
}

export default SelectEventType
